import "../types/index.js";

import { AnimationDuration } from "../constants/animation_duration.js";
import { Direction } from "../constants/direction.js";
import { createCustomEvent } from "../events/custom_event.js";
import { PanEvent, PanGesture } from "../events/pan_event.js";
import { SheetEvent } from "../events/sheet_event.js";
import { animateOnce } from "../utilities/animation.js";
import { createFragment } from "../utilities/elements.js";
import { supportsTouch } from "../utilities/media.js";
import { onResize } from "../utilities/resize.js";
import { nextTick } from "../utilities/scheduling.js";
import {
  getTranslateCoordinates,
  setTranslate,
  translateValueForCoordinates,
} from "../utilities/transform.js";
import { DickTokStandaloneComponent } from "./StandaloneComponent.js";
import { defineElement } from "./registry.js";

export class DickTokSheet extends DickTokStandaloneComponent {
  static attributes = [
    { name: "allow-fullscreen", type: Boolean, optional: true },
  ];

  // Using the dialog element allows the sheet to be a child of the owner
  // while still being displayed in the top layer (in an independent CSS
  // stacking context above all other elements, regardless of the z-index of
  // the parent). This allows events to bubble to the owner for handling.
  static template = createFragment(`
      <dialog data-target="dialog" class="dt-sheet__dialog">
        <dt-pansurface axes='["vertical"]' swipe-velocity="0.4">
          <div
            class="dt-sheet__backdrop"
            data-target="backdrop"
            data-action="${
              // See comment in DickTokPost#setupListeners() on event type
              supportsTouch() ? "touchend" : "click"
            }:dt-sheet->close"
          ></div>
          <div class="dt-sheet__content" data-target="content">
              <slot></slot>
          </div>
        </dt-pansurface>
      </dialog>
    `);

  static targets = ["backdrop", "content", "dialog"];

  #dialogHeight = 0;
  #sheetHeight = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setupListeners();
    // Hack that prevents the opening user interaction from dismissing the
    // modal. Not sure why this was happening, but this solves it for now.
    nextTick().then(() => {
      this.dialogTarget.showModal();
      // Wait until the dialog is visible or the height will be 0
      this.#calculateHeight();
    });
  }

  /**
   * Handles a user interaction to dismiss the modal.
   * @param {Event} event
   */
  close(event) {
    // Prevent the event from bubbling, because the close interaction shouldn't
    // trigger anything outside of the sheet.
    event.stopPropagation();
    // Trigger an event to notify the `SheetService` to close the sheet. The
    // `SheetService` manages the component lifecycle so that it can notify the
    // owner when the sheet is closed. The service calls `animateClose()`.
    this.dispatchEvent(createCustomEvent(SheetEvent.CLOSE));
  }

  /** Animate the sheet closed, called by `SheetService`. */
  async animateClose() {
    const transform = translateValueForCoordinates({
      x: 0,
      y: this.#sheetHeight,
    });
    const slideOutAnimation = this.contentTarget.animate([{ transform }], {
      duration: AnimationDuration.SHORT,
    });
    slideOutAnimation.finished.then(() => {
      this.contentTarget.style.transform = transform;
    });
    await Promise.all([
      animateOnce(this.backdropTarget, "dt-sheet__backdrop--closing"),
      slideOutAnimation.finished,
    ]);
    return this;
  }

  #setupListeners() {
    const options = { passive: true, signal: this.disconnectSignal };
    // Handle all pan events so that they can be cancelled to prevent bubbling
    // because this component can be a child of a feed component which also
    // listens for pan events.
    this.addEventListener(PanEvent.PAN_START, this.#handlePanStart, options);
    this.addEventListener(PanEvent.PAN_MOVE, this.#handlePanMove, options);
    this.addEventListener(PanEvent.PAN_END, this.#handlePanEnd, options);
    this.addEventListener(PanEvent.PAN_CANCEL, this.#handlePanCancel, options);
  }

  #calculateHeight() {
    // Get the inner height to determine how much of the sheet should show,
    // then set the height to 100% of the window so that when the user pans
    // there isn't a gap between the sheet content and the bottom of the screen
    this.#sheetHeight = this.contentTarget.offsetHeight;
    this.contentTarget.style.height = "100dvh";
    this.contentTarget.style.bottom = `calc(${this.#sheetHeight}px - 100%)`;

    // We also need to know the dialog height in order to animate the pan
    this.#dialogHeight = this.dialogTarget.offsetHeight;
    onResize(this.dialogTarget, this.disconnectSignal, ({ contentRect }) => {
      this.#dialogHeight = contentRect.height;
    });
  }

  /** @param {CustomEvent} event */
  #handlePanStart = (event) => {
    event.stopPropagation();
  };

  /** @param {CustomEvent<PanEventDetail>} event */
  #handlePanMove = (event) => {
    event.stopPropagation();
    let y = event.detail.delta.native.y;
    const isPanUp = y < 0;
    // Only allow the sheet to be panned up if `allow-fullscreen` is set
    if (isPanUp && !this.allowFullscreen) {
      return;
    }
    // Prevent the sheet from being panned past the top of the page.
    if (isPanUp && y - this.#sheetHeight < this.#dialogHeight * -1) {
      y = this.#dialogHeight * -1 + this.#sheetHeight;
    }
    setTranslate(this.contentTarget, { x: 0, y });
  };

  /** @param {CustomEvent<PanEventDetail>} event */
  #handlePanEnd = (event) => {
    event.stopPropagation();
    const coordinates = getTranslateCoordinates(this.contentTarget);
    if (
      // Close if the user pans the sheet down more than halfway
      coordinates.y > this.#sheetHeight / 2 ||
      // Close on a swipe down gesture
      (event.detail.gesture === PanGesture.SWIPE &&
        event.detail.direction === Direction.DOWN)
    ) {
      this.close(event);
      return;
    } else {
      const transform = translateValueForCoordinates({ x: 0, y: 0 });
      this.contentTarget
        .animate([{ transform }], { duration: AnimationDuration.SHORT })
        .finished.then(() => {
          this.contentTarget.style.transform = transform;
        });
    }
  };

  /** @param {CustomEvent} event */
  #handlePanCancel = (event) => {
    event.stopPropagation();
    const transform = translateValueForCoordinates({ x: 0, y: 0 });
    this.contentTarget
      .animate([{ transform }], { duration: AnimationDuration.SHORT })
      .finished.then(() => {
        this.contentTarget.style.transform = transform;
      });
  };
}

defineElement("dt-sheet", DickTokSheet);
